import React, { FC, useCallback, useMemo } from 'react';
import { InvestmentRoundingSetting, roundInvestmentShares } from 'shared';
import { twMerge } from 'tailwind-merge';

import { EditIcon } from '../../../../assets/icons/index';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import Button from '../../../../components/button/index';
import { SummaryItem } from '../../../../components/summary-list/index';
import { Tooltip } from '../../../../components/tooltip/index';
import { useFormat } from '../../../../hooks/index';
import { EventFormType, EventTypeBackend, NewExerciseEvent } from '../../../../types/events.types';
import { InvestmentRoundingSettingTitle } from '../../../../types/investment-rounding.types';
import { SharePlanType } from '../../../../types/pool-plans.types';
import { openSelectedCompanyStorageUrlInNewTab } from '../../../../utils/company-storage-url.util';
import { toMathRound } from '../../../../utils/to-math-round.util';
import { toNumber } from '../../../../utils/to-number.util';
import {
  getTemplateSignatoryBaseType,
  getTemplateSignatoryLabelKey,
  GRANTEE,
  templateSignatoryTypeTranslation,
} from '../../../documents/templates/constants/template-signatory-type.constant';
import { useTemplateById } from '../../../documents/templates/hooks/use-template-by-id.hook';
import { EventAggregateDocumentsPreview } from '../components/event-aggregate-documents-preview';
import { EventTransactionAttachedFilesPreview } from '../components/event-transaction-attached-files-preview';
import { ExerciseEventEditPreviewSummary } from '../components/exercise-event-edit-preview-summary';
import {
  GrantSummaryPlainFields,
  GrantSummaryPlainRow,
  GrantSummarySection,
  joinGranteeNamesForDisplay,
} from '../components/grant-event-summary-layout';
import { ReverseVestingSummaryRows } from '../components/reverse-vesting-summary-rows';
import { ChildrenFormProps, EditEventStepVariant } from '../event-form.types';
import { GrantSignatureOption } from '../event-form.validation';
import {
  mergeGrantSignatoryAssignmentsForDisplay,
  resolveGrantSignatoryDisplayName,
} from '../utils/grant-signatory-display';

export type EditPreviewProps = {
  issuedShares: number;
  onEditClick: (step: EditEventStepVariant) => void;
  previewMode?: true;
  transactionId?: string;
} & ChildrenFormProps;

export const EditPreview: FC<EditPreviewProps> = ({
  companyId: _companyId,
  handleCloseModal,
  formData,
  event,
  onEditClick,
  previewMode,
  issuedShares,
  transactionId,
}) => {
  const { initialStep, stepOne, optionalStep, stepTwo } = formData();
  const eventDetails = stepOne?.eventDetails;
  const { format } = useFormat();
  const handleDocumentClick = (link: string) => {
    void openSelectedCompanyStorageUrlInNewTab(link);
  };

  const grantFormData = eventDetails?.grant;
  const { template: grantTemplate } = useTemplateById(grantFormData?.selectedTemplateId ?? null);
  const isGrantWithSignature =
    grantFormData?.signatureOption === GrantSignatureOption.SIGNATURE &&
    !!grantFormData?.selectedTemplateId;

  const documentsAndNotesEditStep: EditEventStepVariant =
    initialStep?.type === EventFormType.GRANT ? (isGrantWithSignature ? 5 : 3) : 3;

  const grantTemplateEditStep: EditEventStepVariant = isGrantWithSignature ? 3 : 1;
  const grantRecipientsEditStep: EditEventStepVariant = isGrantWithSignature ? 4 : 3;

  const signatoryAssignmentsForDisplay = useMemo(
    () => mergeGrantSignatoryAssignmentsForDisplay(grantFormData?.signatoryAssignments, event),
    [grantFormData?.signatoryAssignments, event],
  );

  const grantRecipientsSummary = useMemo(() => {
    if (!isGrantWithSignature || !grantTemplate || !grantFormData?.grantItems) return null;
    const grantItems = grantFormData.grantItems;
    const orderedTypes = [...grantTemplate.signatoryTypes].sort(
      (a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0),
    );
    const signatoryLabelCounts: Record<string, number> = {};
    orderedTypes.forEach((st) => {
      const labelKey =
        getTemplateSignatoryLabelKey(st.stakeholderType) ??
        templateSignatoryTypeTranslation[GRANTEE];
      signatoryLabelCounts[labelKey] = (signatoryLabelCounts[labelKey] ?? 0) + 1;
    });
    const signatoryLabelSeen: Record<string, number> = {};
    return orderedTypes.map((st) => {
      const baseSignatoryType = getTemplateSignatoryBaseType(st.stakeholderType);
      const labelKey =
        getTemplateSignatoryLabelKey(st.stakeholderType) ??
        templateSignatoryTypeTranslation[GRANTEE];
      signatoryLabelSeen[labelKey] = (signatoryLabelSeen[labelKey] ?? 0) + 1;
      const shouldShowIndex = signatoryLabelCounts[labelKey] > 1;
      const label = (
        <>
          <AppFormattedMessage id={labelKey} />
          {shouldShowIndex ? ` ${signatoryLabelSeen[labelKey]}` : ''}
        </>
      );
      const granteeNames = grantItems.map((item) => item.stakeholder.fullName);
      const value =
        baseSignatoryType === GRANTEE
          ? joinGranteeNamesForDisplay(granteeNames)
          : resolveGrantSignatoryDisplayName(st.id, signatoryAssignmentsForDisplay, grantItems);
      const isGranteeRow = baseSignatoryType === GRANTEE;
      return { isGranteeRow, label, value };
    });
  }, [
    isGrantWithSignature,
    grantTemplate,
    grantFormData?.grantItems,
    signatoryAssignmentsForDisplay,
  ]);

  const calculateShares = useCallback(
    (index: number) => {
      const item = optionalStep?.['fundraising-round']?.investmentItems?.[index];

      const investment = toNumber(item?.investment);

      const sharePrice = toNumber(stepOne?.eventDetails?.['fundraising-round']?.event?.sharePrice);

      const isCompanyShouldHaveOverride =
        sharePrice === 8.540472594017048 && investment === 8541397;

      const roundingSetting =
        stepOne?.eventDetails?.['fundraising-round']?.roundingSetting ??
        InvestmentRoundingSetting.DEFAULT;

      if (investment && sharePrice) {
        const shares = roundInvestmentShares(investment, sharePrice, roundingSetting);
        return isCompanyShouldHaveOverride ? shares - 8 : shares;
      }

      return 0;
    },
    [optionalStep, stepOne?.eventDetails],
  );

  const isRenderGrant =
    initialStep?.type === EventFormType.GRANT &&
    eventDetails?.grant?.enabled &&
    eventDetails?.grant?.grantItems;

  const isRenderFundraising =
    initialStep?.type === EventFormType.FUNDRAISING_ROUND &&
    eventDetails?.['fundraising-round']?.enabled &&
    optionalStep?.['fundraising-round']?.investmentItems;

  const isRenderShareIssuance =
    initialStep?.type === EventFormType.SHARE_ISSUANCE &&
    eventDetails?.['share-issuance']?.enabled &&
    optionalStep?.['share-issuance']?.issuanceItems;

  const isRenderBuyBack =
    initialStep?.type === EventFormType.BUYBACK &&
    eventDetails?.buyback?.enabled &&
    eventDetails?.buyback?.buyBackItems;

  const isRenderSecondaries =
    initialStep?.type === EventFormType.SECONDARIES &&
    eventDetails?.secondaries?.enabled &&
    eventDetails?.secondaries?.secondariesItems;

  const isRenderClassConversion =
    initialStep?.type === EventFormType.CONVERSION &&
    eventDetails?.['conversion']?.enabled &&
    eventDetails?.['conversion']?.classConversionItems;

  const isRenderValuation =
    initialStep?.type === EventFormType.VALUATION && eventDetails?.valuation?.enabled;

  const isRenderExerciseLike =
    !!event &&
    (event.type === EventTypeBackend.EXERCISE || event.type === EventTypeBackend.APPROVAL) &&
    'exercise' in event &&
    Boolean(event.exercise?.items?.length);

  const showAggregateDocumentsNotes =
    !isRenderExerciseLike &&
    (!transactionId ||
      (event?.type !== EventTypeBackend.EXERCISE && event?.type !== EventTypeBackend.APPROVAL));

  const canPatchExerciseEventLevelDocuments =
    isRenderExerciseLike &&
    event.exercise.items.every(
      ({ vestingTask }) =>
        vestingTask.grantItem.plan.type === SharePlanType.STOCK_OPTIONS ||
        vestingTask.grantItem.plan.type === SharePlanType.BSPCE,
    );

  const showDocumentsNotesEditActions =
    !previewMode &&
    !isRenderFundraising &&
    (!isRenderExerciseLike || canPatchExerciseEventLevelDocuments);

  const valuationEvent = eventDetails?.valuation;
  const preMoneyValuation =
    valuationEvent?.sharePrice && valuationEvent?.issuedSharesOnStart !== undefined
      ? valuationEvent?.sharePrice * valuationEvent?.issuedSharesOnStart
      : valuationEvent?.sharePrice
        ? valuationEvent?.sharePrice * issuedShares
        : 0;

  const roundedSharePrice = toMathRound(
    eventDetails?.valuation?.sharePrice ||
      eventDetails?.['share-issuance']?.event?.sharePrice ||
      eventDetails?.['fundraising-round']?.event.sharePrice,
  );

  if (!eventDetails) return null;

  return (
    <>
      <div className="invisible-y-scroll flex min-h-0 flex-1 flex-col rounded-b-xl border-b border-gray-300 bg-white p-6 pt-0">
        <div className="flex w-full shrink-0 flex-col gap-4">
          {isRenderGrant && (
            <div className="flex h-fit flex-col gap-4">
              <GrantSummarySection
                action={
                  !previewMode ? (
                    <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                      <EditIcon iconColor="#2565C8" />
                    </Button>
                  ) : undefined
                }
                title={<AppFormattedMessage id="GENERAL" />}
              >
                <SummaryItem
                  className="border-none py-0 pb-1 pt-1"
                  title={<AppFormattedMessage id="PLAN_NAME" />}
                  titleClassName="w-fit"
                  titleWrapperClassName="w-[120px]"
                  value={
                    <Tooltip content={stepOne?.eventDetails?.grant?.sharePlan?.name ?? ''}>
                      {stepOne?.eventDetails?.grant?.sharePlan?.name ?? '—'}
                    </Tooltip>
                  }
                />
              </GrantSummarySection>

              {(transactionId
                ? (stepOne?.eventDetails?.grant?.grantItems ?? []).filter(
                    (item) => item.id === transactionId,
                  )
                : (stepOne?.eventDetails?.grant?.grantItems ?? [])
              ).map((item, index) => (
                <GrantSummarySection
                  action={
                    !previewMode ? (
                      <Button className="w-fit" onClick={() => onEditClick(2)} styleType="NONE">
                        <EditIcon iconColor="#2565C8" />
                      </Button>
                    ) : undefined
                  }
                  key={item.id ?? index}
                  title={
                    <Tooltip
                      childrenWrapperClassName="max-w-[380px]"
                      content={item.stakeholder.fullName}
                    >
                      <span className="truncate">
                        {<AppFormattedMessage id="GRANT" />} - {item.stakeholder.fullName}
                      </span>
                    </Tooltip>
                  }
                >
                  <SummaryItem
                    className="border-b-[1px] border-gray-100 pt-0"
                    title={<AppFormattedMessage id="DATE" />}
                    titleClassName="w-fit"
                    titleWrapperClassName="w-[120px]"
                    value={item.grantDate ? format(item.grantDate, 'dd/MM/yyyy') : '-'}
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="STAKEHOLDER" />}
                    titleClassName="w-fit"
                    titleWrapperClassName="w-[120px]"
                    value={
                      <Tooltip content={item.stakeholder.fullName}>
                        {item.stakeholder.fullName}
                      </Tooltip>
                    }
                  />
                  <SummaryItem
                    className="border-b-[1px] border-gray-100"
                    title={<AppFormattedMessage id="NO_OF_SHARES" />}
                    titleClassName="w-fit"
                    titleWrapperClassName="w-[120px]"
                    value={toNumber(item.shares).toLocaleString('en-US') || ''}
                  />
                  <EventTransactionAttachedFilesPreview
                    event={event}
                    files={item.files}
                    onDocumentClick={handleDocumentClick}
                    previewMode={previewMode}
                    transactionId={item.id}
                  />
                </GrantSummarySection>
              ))}

              {isGrantWithSignature && grantTemplate && (
                <GrantSummarySection
                  action={
                    !previewMode ? (
                      <Button
                        className="w-fit"
                        onClick={() => onEditClick(grantTemplateEditStep)}
                        styleType="NONE"
                      >
                        <EditIcon iconColor="#2565C8" />
                      </Button>
                    ) : undefined
                  }
                  title={<AppFormattedMessage id="DOCUMENT_TEMPLATE" />}
                >
                  <SummaryItem
                    className="border-none py-0 pb-1 pt-1"
                    title={<AppFormattedMessage id="NAME" />}
                    titleClassName="w-fit"
                    titleWrapperClassName="w-[120px]"
                    value={
                      <Tooltip content={grantTemplate.name}>
                        <span className="truncate">{grantTemplate.name}</span>
                      </Tooltip>
                    }
                  />
                </GrantSummarySection>
              )}

              {grantRecipientsSummary && grantRecipientsSummary.length > 0 && (
                <GrantSummarySection
                  action={
                    !previewMode ? (
                      <Button
                        className="w-fit"
                        onClick={() => onEditClick(grantRecipientsEditStep)}
                        styleType="NONE"
                      >
                        <EditIcon iconColor="#2565C8" />
                      </Button>
                    ) : undefined
                  }
                  title={<AppFormattedMessage id="RECIPIENTS" />}
                >
                  {grantRecipientsSummary.map(({ label, value }, i) => (
                    <SummaryItem
                      className={twMerge(
                        i < grantRecipientsSummary.length - 1 && 'border-b-[1px] border-gray-100',
                        i === 0 && 'pt-0',
                        i === grantRecipientsSummary.length - 1 && 'pb-0',
                      )}
                      key={i}
                      title={label}
                      titleClassName="w-fit"
                      titleWrapperClassName="w-[120px]"
                      value={value}
                    />
                  ))}
                </GrantSummarySection>
              )}
            </div>
          )}

          {isRenderExerciseLike && (
            <ExerciseEventEditPreviewSummary
              documentsEditAction={
                showDocumentsNotesEditActions ? (
                  <Button
                    className="w-fit shrink-0"
                    onClick={() => onEditClick(documentsAndNotesEditStep)}
                    styleType="NONE"
                  >
                    <EditIcon iconColor="#2565C8" />
                  </Button>
                ) : undefined
              }
              event={event as NewExerciseEvent}
              format={format}
              previewMode={previewMode}
              transactionId={transactionId}
            />
          )}

          {isRenderFundraising && (
            <div className="flex h-fit flex-col gap-4">
              {transactionId ? (
                <>
                  {optionalStep?.['fundraising-round']?.investmentItems.map((item, index) => {
                    if (item.id !== transactionId) return null;

                    return (
                      <div
                        className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                        key={index}
                      >
                        <div className="flex w-full justify-between px-4">
                          <Tooltip
                            childrenWrapperClassName="max-w-[380px]"
                            content={item.stakeholder.fullName}
                          >
                            <span className="truncate text-sm font-[550] text-gray-700">
                              <AppFormattedMessage id="INVESTMENT" /> - {item.stakeholder.fullName}
                            </span>
                          </Tooltip>
                          {!previewMode && stepOne.eventDetails['fundraising-round']?.isOpen && (
                            <Button
                              className="w-fit"
                              onClick={() => onEditClick(2)}
                              styleType="NONE"
                            >
                              <EditIcon iconColor="#2565C8" />
                            </Button>
                          )}
                        </div>
                        <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                          <SummaryItem
                            className="border-b-[1px] border-gray-100 pt-0"
                            title={<AppFormattedMessage id="STAKEHOLDER" />}
                            value={
                              <Tooltip content={item.stakeholder.fullName}>
                                {item.stakeholder.fullName}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="INVESTMENT" />}
                            value={toNumber(item.investment).toLocaleString('en-US') || ''}
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="SHARE_CLASS" />}
                            value={
                              <Tooltip content={item.shareClass.name}>
                                {item.shareClass.name}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="NO_OF_SHARES" />}
                            value={
                              calculateShares(index)
                                ? calculateShares(index).toLocaleString('en-US')
                                : '-'
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="INVEST_DATE" />}
                            value={
                              item.investmentDate ? format(item.investmentDate, 'dd/MM/yyyy') : '-'
                            }
                          />
                          <EventTransactionAttachedFilesPreview
                            event={event}
                            files={item.files}
                            onDocumentClick={handleDocumentClick}
                            previewMode={previewMode}
                            transactionId={item.id}
                          />
                        </div>
                      </div>
                    );
                  })}
                </>
              ) : (
                <>
                  <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
                    <div className="flex w-full justify-between px-4">
                      <span className="text-sm font-[550] text-gray-700">
                        <AppFormattedMessage id="GENERAL" />
                      </span>
                      {!previewMode && (
                        <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                          <EditIcon iconColor="#2565C8" />
                        </Button>
                      )}
                    </div>
                    <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                      <div className="flex w-full gap-3 border-b-[1px] border-gray-100 py-3">
                        <span className="w-[120px] flex-shrink-0 text-xs font-[450] text-gray-500">
                          <AppFormattedMessage id="ROUND_STATUS" />:
                        </span>
                        <span
                          className={twMerge(
                            'rounded-2xl px-2 py-[2px] text-xs font-medium',
                            stepOne.eventDetails['fundraising-round']?.isOpen
                              ? 'bg-forest-50 text-forest-600'
                              : 'bg-fireside-50 text-fireside-600',
                          )}
                        >
                          {stepOne.eventDetails['fundraising-round']?.isOpen ? (
                            <AppFormattedMessage id="OPEN" />
                          ) : (
                            <AppFormattedMessage id="CLOSED" />
                          )}
                        </span>
                      </div>
                      <SummaryItem
                        className="border-b-[1px] border-gray-100"
                        title={<AppFormattedMessage id="ROUND_NAME" />}
                        value={stepOne.eventDetails['fundraising-round']?.event.name || ''}
                      />
                      <SummaryItem
                        className="border-b-[1px] border-gray-100"
                        title={
                          stepOne.eventDetails['fundraising-round']?.isOpen ? (
                            <AppFormattedMessage id="OPEN_DATE" />
                          ) : (
                            <AppFormattedMessage id="CLOSING_DATE" />
                          )
                        }
                        value={
                          stepOne.eventDetails['fundraising-round']?.date
                            ? format(stepOne.eventDetails['fundraising-round']?.date, 'dd/MM/yyyy')
                            : '-'
                        }
                      />
                      <SummaryItem
                        className="border-b-[1px] border-gray-100"
                        title={<AppFormattedMessage id="SHARES_PRICE" />}
                        value={roundedSharePrice.toLocaleString('en-US')}
                      />
                      <SummaryItem
                        className="border-b-[1px] border-gray-100"
                        title={<AppFormattedMessage id="ROUNDING_SETTINGS" />}
                        value={
                          stepOne.eventDetails['fundraising-round']?.roundingSetting ? (
                            <AppFormattedMessage
                              id={
                                InvestmentRoundingSettingTitle[
                                  stepOne.eventDetails['fundraising-round'].roundingSetting
                                ]
                              }
                            />
                          ) : (
                            '-'
                          )
                        }
                      />
                      <SummaryItem
                        className="pb-0"
                        title={<AppFormattedMessage id="PRE_MONEY_VALUATION" />}
                        value={(stepOne?.eventDetails?.['fundraising-round']?.event?.sharePrice
                          ? stepOne.eventDetails['fundraising-round'].event.sharePrice *
                            (stepOne.eventDetails['fundraising-round'].event.issuedSharesOnStart ??
                              0)
                          : 0
                        ).toLocaleString('en-US')}
                      />
                    </div>
                  </div>
                  {optionalStep?.['fundraising-round']?.investmentItems.map((item, index) => (
                    <div
                      className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                      key={index}
                    >
                      <div className="flex w-full justify-between px-4">
                        <Tooltip
                          childrenWrapperClassName="max-w-[380px]"
                          content={item.stakeholder.fullName}
                        >
                          <span className="truncate text-sm font-[550] text-gray-700">
                            <AppFormattedMessage id="INVESTMENT" /> - {item.stakeholder.fullName}
                          </span>
                        </Tooltip>
                        {!previewMode && stepOne.eventDetails['fundraising-round']?.isOpen && (
                          <Button className="w-fit" onClick={() => onEditClick(2)} styleType="NONE">
                            <EditIcon iconColor="#2565C8" />
                          </Button>
                        )}
                      </div>
                      <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                        <SummaryItem
                          className="border-b-[1px] border-gray-100 pt-0"
                          title={<AppFormattedMessage id="STAKEHOLDER" />}
                          value={
                            <Tooltip content={item.stakeholder.fullName}>
                              {item.stakeholder.fullName}
                            </Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="INVESTMENT" />}
                          value={toNumber(item.investment).toLocaleString('en-US') || ''}
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="SHARE_CLASS" />}
                          value={
                            <Tooltip content={item.shareClass.name}>{item.shareClass.name}</Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="NO_OF_SHARES" />}
                          value={
                            calculateShares(index)
                              ? calculateShares(index).toLocaleString('en-US')
                              : '-'
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="INVEST_DATE" />}
                          value={
                            item.investmentDate ? format(item.investmentDate, 'dd/MM/yyyy') : '-'
                          }
                        />
                        <EventTransactionAttachedFilesPreview
                          event={event}
                          files={item.files}
                          onDocumentClick={handleDocumentClick}
                          previewMode={previewMode}
                          transactionId={item.id}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {isRenderShareIssuance && (
            <div className="flex h-fit flex-col gap-4">
              {transactionId ? (
                <>
                  {optionalStep?.['share-issuance']?.issuanceItems
                    .filter((item) => item.id === transactionId)
                    .map((item, index) => (
                      <div
                        className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                        key={index}
                      >
                        <div className="flex w-full justify-between px-4">
                          <Tooltip
                            childrenWrapperClassName="max-w-[380px]"
                            content={item.stakeholder.fullName}
                          >
                            <span className="truncate text-sm font-[550] text-gray-700">
                              <AppFormattedMessage id="ISSUANCE" /> - {item.stakeholder.fullName}
                            </span>
                          </Tooltip>
                          {!previewMode && (
                            <Button
                              className="w-fit"
                              onClick={() => onEditClick(2)}
                              styleType="NONE"
                            >
                              <EditIcon iconColor="#2565C8" />
                            </Button>
                          )}
                        </div>
                        <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                          <SummaryItem
                            className="border-b-[1px] border-gray-100 pt-0"
                            title={<AppFormattedMessage id="STAKEHOLDER" />}
                            value={
                              <Tooltip content={item.stakeholder.fullName}>
                                {item.stakeholder.fullName}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="SHARE_CLASS" />}
                            value={
                              <Tooltip content={item.shareClass.name}>
                                {item.shareClass.name}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="NO_OF_SHARES" />}
                            value={toNumber(item.shares)?.toLocaleString('en-US') || ''}
                          />
                          <ReverseVestingSummaryRows
                            hasRowsBelow
                            reverseVesting={item.reverseVesting}
                          />
                          <EventTransactionAttachedFilesPreview
                            event={event}
                            files={item.files}
                            onDocumentClick={handleDocumentClick}
                            previewMode={previewMode}
                            transactionId={item.id}
                          />
                        </div>
                      </div>
                    ))}
                </>
              ) : (
                <>
                  <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
                    <span className="px-4 text-sm font-[550] text-gray-700">
                      <AppFormattedMessage id="GENERAL" />
                    </span>
                    <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                      <SummaryItem
                        className="border-b-[1px] border-gray-100 pt-0"
                        title={<AppFormattedMessage id="EVENT_NAME" />}
                        value={stepOne.eventDetails['share-issuance']?.event.name || ''}
                      />
                      <SummaryItem
                        className="border-b-[1px] border-gray-100"
                        title={<AppFormattedMessage id="DATE" />}
                        value={
                          stepOne.eventDetails['share-issuance']?.date
                            ? format(stepOne.eventDetails['share-issuance']?.date, 'dd/MM/yyyy')
                            : '-'
                        }
                      />
                      <SummaryItem
                        className="pb-0"
                        title={<AppFormattedMessage id="SHARES_PRICE" />}
                        value={roundedSharePrice.toLocaleString('en-US')}
                      />
                    </div>
                  </div>
                  {optionalStep?.['share-issuance']?.issuanceItems.map((item, index) => (
                    <div
                      className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                      key={index}
                    >
                      <div className="flex w-full justify-between px-4">
                        <Tooltip
                          childrenWrapperClassName="max-w-[380px]"
                          content={item.stakeholder.fullName}
                        >
                          <span className="truncate text-sm font-[550] text-gray-700">
                            <AppFormattedMessage id="ISSUANCE" /> - {item.stakeholder.fullName}
                          </span>
                        </Tooltip>
                        {!previewMode && (
                          <Button className="w-fit" onClick={() => onEditClick(2)} styleType="NONE">
                            <EditIcon iconColor="#2565C8" />
                          </Button>
                        )}
                      </div>
                      <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                        <SummaryItem
                          className="border-b-[1px] border-gray-100 pt-0"
                          title={<AppFormattedMessage id="STAKEHOLDER" />}
                          value={
                            <Tooltip content={item.stakeholder.fullName}>
                              {item.stakeholder.fullName}
                            </Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="SHARE_CLASS" />}
                          value={
                            <Tooltip content={item.shareClass.name}>{item.shareClass.name}</Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="NO_OF_SHARES" />}
                          value={toNumber(item.shares)?.toLocaleString('en-US') || ''}
                        />
                        <ReverseVestingSummaryRows
                          hasRowsBelow
                          reverseVesting={item.reverseVesting}
                        />
                        <EventTransactionAttachedFilesPreview
                          event={event}
                          files={item.files}
                          onDocumentClick={handleDocumentClick}
                          previewMode={previewMode}
                          transactionId={item.id}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {isRenderBuyBack && (
            <div className="flex h-fit flex-col gap-4">
              {transactionId ? (
                <>
                  {stepOne?.eventDetails?.buyback?.buyBackItems
                    .filter((item) => item.id === transactionId)
                    .map((item, index) => (
                      <div
                        className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                        key={index}
                      >
                        <div className="flex w-full justify-between px-4">
                          <Tooltip
                            childrenWrapperClassName="max-w-[380px]"
                            content={item.stakeholder.fullName}
                          >
                            <span className="truncate text-sm font-[550] text-gray-700">
                              <AppFormattedMessage id="STAKEHOLDER" /> - {item.stakeholder.fullName}
                            </span>
                          </Tooltip>
                          {!previewMode && (
                            <Button
                              className="w-fit"
                              onClick={() => onEditClick(1)}
                              styleType="NONE"
                            >
                              <EditIcon iconColor="#2565C8" />
                            </Button>
                          )}
                        </div>
                        <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                          <SummaryItem
                            className="border-b-[1px] border-gray-100 pt-0"
                            title="Event Date"
                            value={item ? format(item.date, 'dd/MM/yyyy') : '-'}
                          />

                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="STAKEHOLDER" />}
                            value={
                              <Tooltip content={item.stakeholder.fullName}>
                                {item.stakeholder.fullName}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="SHARE_CLASS" />}
                            value={
                              <Tooltip content={item.shareClass.name}>
                                {item.shareClass.name}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="NO_OF_SHARES" />}
                            value={toNumber(item.shares).toLocaleString('en-US') || ''}
                          />
                          <EventTransactionAttachedFilesPreview
                            event={event}
                            files={item.files}
                            onDocumentClick={handleDocumentClick}
                            previewMode={previewMode}
                            transactionId={item.id}
                          />
                        </div>
                      </div>
                    ))}
                </>
              ) : (
                <>
                  {stepOne?.eventDetails?.buyback?.buyBackItems.map((item, index) => (
                    <div
                      className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                      key={index}
                    >
                      <div className="flex w-full justify-between px-4">
                        <Tooltip
                          childrenWrapperClassName="max-w-[380px]"
                          content={item.stakeholder.fullName}
                        >
                          <span className="truncate text-sm font-[550] text-gray-700">
                            <AppFormattedMessage id="STAKEHOLDER" /> - {item.stakeholder.fullName}
                          </span>
                        </Tooltip>
                        {!previewMode && (
                          <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                            <EditIcon iconColor="#2565C8" />
                          </Button>
                        )}
                      </div>
                      <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                        <SummaryItem
                          className="border-b-[1px] border-gray-100 pt-0"
                          title="Event Date"
                          value={item ? format(item.date, 'dd/MM/yyyy') : '-'}
                        />

                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="STAKEHOLDER" />}
                          value={
                            <Tooltip content={item.stakeholder.fullName}>
                              {item.stakeholder.fullName}
                            </Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="SHARE_CLASS" />}
                          value={
                            <Tooltip content={item.shareClass.name}>{item.shareClass.name}</Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="NO_OF_SHARES" />}
                          value={toNumber(item.shares).toLocaleString('en-US') || ''}
                        />
                        <EventTransactionAttachedFilesPreview
                          event={event}
                          files={item.files}
                          onDocumentClick={handleDocumentClick}
                          previewMode={previewMode}
                          transactionId={item.id}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {isRenderSecondaries && (
            <div className="flex h-fit flex-col gap-4">
              {transactionId ? (
                <>
                  {stepOne?.eventDetails?.secondaries?.secondariesItems
                    .filter((item) => item.id === transactionId)
                    .map((item, index) => (
                      <div
                        className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                        key={index}
                      >
                        <div className="flex w-full justify-between px-4">
                          <Tooltip
                            childrenWrapperClassName="max-w-[380px]"
                            content={`${item.stakeholderFrom.fullName} to ${item.stakeholderTo.fullName}`}
                          >
                            <span className="truncate text-sm font-[550] text-gray-700">
                              <AppFormattedMessage id="STAKEHOLDER" /> -{' '}
                              {item.stakeholderFrom.fullName} to {item.stakeholderTo.fullName}
                            </span>
                          </Tooltip>
                          {!previewMode && (
                            <Button
                              className="w-fit"
                              onClick={() => onEditClick(1)}
                              styleType="NONE"
                            >
                              <EditIcon iconColor="#2565C8" />
                            </Button>
                          )}
                        </div>
                        <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                          <SummaryItem
                            className="border-b-[1px] border-gray-100 pt-0"
                            title={<AppFormattedMessage id="EVENT_DATE" />}
                            value={item.date ? format(item.date, 'dd/MM/yyyy') : '-'}
                          />

                          <Tooltip content={item.stakeholderFrom.fullName}>
                            <SummaryItem
                              className="border-b-[1px] border-gray-100"
                              title={<AppFormattedMessage id="FROM_STAKEHOLDER" />}
                              value={item.stakeholderFrom.fullName}
                            />
                          </Tooltip>
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="TO_STAKEHOLDER" />}
                            value={
                              <Tooltip content={item.stakeholderTo.fullName}>
                                {item.stakeholderTo.fullName}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="SHARE_CLASS" />}
                            value={
                              <Tooltip content={item.shareClass.name}>
                                {item.shareClass.name}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="NO_OF_SHARES" />}
                            value={toNumber(item.shares).toLocaleString('en-US') || ''}
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="SHARE_VALUE" />}
                            value={toNumber(item.shareValue).toLocaleString('en-US') || ''}
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="TRANSFER_AMOUNT" />}
                            value={toNumber(item.shareValue * item.shares).toLocaleString('en-US')}
                          />
                          <EventTransactionAttachedFilesPreview
                            event={event}
                            files={item.files}
                            onDocumentClick={handleDocumentClick}
                            previewMode={previewMode}
                            transactionId={item.id}
                          />
                        </div>
                      </div>
                    ))}
                </>
              ) : (
                <>
                  {stepOne?.eventDetails?.secondaries?.secondariesItems.map((item, index) => (
                    <div
                      className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                      key={index}
                    >
                      <div className="flex w-full justify-between px-4">
                        <Tooltip
                          childrenWrapperClassName="max-w-[380px]"
                          content={`${item.stakeholderFrom.fullName} to ${item.stakeholderTo.fullName}`}
                        >
                          <span className="truncate text-sm font-[550] text-gray-700">
                            <AppFormattedMessage id="STAKEHOLDER" /> -{' '}
                            {item.stakeholderFrom.fullName} to {item.stakeholderTo.fullName}
                          </span>
                        </Tooltip>
                        {!previewMode && (
                          <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                            <EditIcon iconColor="#2565C8" />
                          </Button>
                        )}
                      </div>
                      <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                        <SummaryItem
                          className="border-b-[1px] border-gray-100 pt-0"
                          title={<AppFormattedMessage id="EVENT_DATE" />}
                          value={item.date ? format(item.date, 'dd/MM/yyyy') : '-'}
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="FROM_STAKEHOLDER" />}
                          value={
                            <Tooltip content={item.stakeholderFrom.fullName}>
                              {item.stakeholderFrom.fullName}
                            </Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="TO_STAKEHOLDER" />}
                          value={
                            <Tooltip content={item.stakeholderTo.fullName}>
                              {item.stakeholderTo.fullName}
                            </Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="SHARE_CLASS" />}
                          value={
                            <Tooltip content={item.shareClass.name}>{item.shareClass.name}</Tooltip>
                          }
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="NO_OF_SHARES" />}
                          value={toNumber(item.shares).toLocaleString('en-US') || ''}
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="SHARE_VALUE" />}
                          value={toNumber(item.shareValue).toLocaleString('en-US') || ''}
                        />
                        <SummaryItem
                          className="border-b-[1px] border-gray-100"
                          title={<AppFormattedMessage id="TRANSFER_AMOUNT" />}
                          value={toNumber(item.shareValue * item.shares).toLocaleString('en-US')}
                        />
                        <EventTransactionAttachedFilesPreview
                          event={event}
                          files={item.files}
                          onDocumentClick={handleDocumentClick}
                          previewMode={previewMode}
                          transactionId={item.id}
                        />
                      </div>
                    </div>
                  ))}
                </>
              )}
            </div>
          )}

          {isRenderClassConversion && (
            <div className="flex h-fit flex-col gap-4">
              {transactionId ? (
                <>
                  {stepOne?.eventDetails?.['conversion']?.classConversionItems
                    .filter((item) => item.id === transactionId)
                    .map((item, index) => (
                      <div
                        className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                        key={index}
                      >
                        <div className="flex w-full justify-between px-4">
                          <Tooltip
                            childrenWrapperClassName="max-w-[380px]"
                            content={item.stakeholder.fullName}
                          >
                            <span className="truncate text-sm font-[550] text-gray-700">
                              <AppFormattedMessage id="CONVERSION" /> - {item.stakeholder.fullName}
                            </span>
                          </Tooltip>
                          {!previewMode && (
                            <Button
                              className="w-fit"
                              onClick={() => onEditClick(1)}
                              styleType="NONE"
                            >
                              <EditIcon iconColor="#2565C8" />
                            </Button>
                          )}
                        </div>
                        <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                          <SummaryItem
                            className="border-b-[1px] border-gray-100 pt-0"
                            title={<AppFormattedMessage id="STAKEHOLDER" />}
                            value={
                              <Tooltip content={item.stakeholder.fullName}>
                                {item.stakeholder.fullName}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="FROM_SHARE_CLASS" />}
                            value={
                              <Tooltip content={item.shareClassFrom.name}>
                                {item.shareClassFrom.name}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="TO_SHARE_CLASS" />}
                            value={
                              <Tooltip content={item.shareClassTo.name}>
                                {item.shareClassTo.name}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="NO_OF_SHARES" />}
                            value={toNumber(item.shares).toLocaleString('en-US') || ''}
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="SHARE_VALUE" />}
                            value={toNumber(
                              item.shares * item.shareClassFrom.conversionRatio,
                            ).toLocaleString('en-US')}
                          />
                          <SummaryItem
                            className="pb-0"
                            title={<AppFormattedMessage id="CONVERSION_RATIO" />}
                            value={`${item.shareClassFrom.conversionRatio}x`}
                          />
                        </div>
                      </div>
                    ))}
                </>
              ) : (
                <>
                  {stepOne?.eventDetails?.['conversion']?.classConversionItems.map(
                    (item, index) => (
                      <div
                        className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2"
                        key={index}
                      >
                        <div className="flex w-full justify-between px-4">
                          <Tooltip
                            childrenWrapperClassName="max-w-[380px]"
                            content={item.stakeholder.fullName}
                          >
                            <span className="truncate text-sm font-[550] text-gray-700">
                              <AppFormattedMessage id="CONVERSION" /> - {item.stakeholder.fullName}
                            </span>
                          </Tooltip>
                          {!previewMode && (
                            <Button
                              className="w-fit"
                              onClick={() => onEditClick(1)}
                              styleType="NONE"
                            >
                              <EditIcon iconColor="#2565C8" />
                            </Button>
                          )}
                        </div>
                        <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                          <SummaryItem
                            className="border-b-[1px] border-gray-100 pt-0"
                            title={<AppFormattedMessage id="STAKEHOLDER" />}
                            value={
                              <Tooltip content={item.stakeholder.fullName}>
                                {item.stakeholder.fullName}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="FROM_SHARE_CLASS" />}
                            value={
                              <Tooltip content={item.shareClassFrom.name}>
                                {item.shareClassFrom.name}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="TO_SHARE_CLASS" />}
                            value={
                              <Tooltip content={item.shareClassTo.name}>
                                {item.shareClassTo.name}
                              </Tooltip>
                            }
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="NO_OF_SHARES" />}
                            value={toNumber(item.shares).toLocaleString('en-US') || ''}
                          />
                          <SummaryItem
                            className="border-b-[1px] border-gray-100"
                            title={<AppFormattedMessage id="SHARE_VALUE" />}
                            value={toNumber(
                              item.shares * item.shareClassFrom.conversionRatio,
                            ).toLocaleString('en-US')}
                          />
                          <SummaryItem
                            className="pb-0"
                            title={<AppFormattedMessage id="CONVERSION_RATIO" />}
                            value={`${item.shareClassFrom.conversionRatio}x`}
                          />
                        </div>
                      </div>
                    ),
                  )}
                </>
              )}
            </div>
          )}

          {isRenderValuation && (
            <div className="flex h-fit flex-col gap-2 rounded-lg bg-gray-50 pt-2">
              <div className="flex w-full justify-between px-4">
                <span className="text-sm font-[550] text-gray-700">Valuation</span>
                {!previewMode && (
                  <Button className="w-fit" onClick={() => onEditClick(1)} styleType="NONE">
                    <EditIcon iconColor="#2565C8" />
                  </Button>
                )}
              </div>
              <div className="rounded-b-lg border-[1px] border-gray-100 bg-white px-4 pb-3 pt-4">
                <SummaryItem
                  className="border-b-[1px] border-gray-100 pt-0"
                  title={<AppFormattedMessage id="EVENT_DATE" />}
                  value={
                    stepOne.eventDetails?.valuation?.date
                      ? format(stepOne.eventDetails?.valuation?.date, 'dd/MM/yyyy')
                      : '-'
                  }
                />
                <SummaryItem
                  className="border-b-[1px] border-gray-100"
                  title={<AppFormattedMessage id="EVENT_NAME" />}
                  value={
                    <Tooltip content={stepOne.eventDetails?.valuation?.name}>
                      {stepOne.eventDetails?.valuation?.name}
                    </Tooltip>
                  }
                />
                <SummaryItem
                  className="border-b-[1px] border-gray-100"
                  title={<AppFormattedMessage id="SHARES_PRICE" />}
                  value={roundedSharePrice.toLocaleString('en-US')}
                />
                <SummaryItem
                  className="pb-0"
                  title={<AppFormattedMessage id="PRE_MONEY_VALUATION" />}
                  value={toNumber(preMoneyValuation).toLocaleString('en-US')}
                />
              </div>
            </div>
          )}

          {showAggregateDocumentsNotes && (
            <>
              <EventAggregateDocumentsPreview
                editAction={
                  <>
                    {isRenderFundraising &&
                      stepOne.eventDetails['fundraising-round']?.isOpen &&
                      !previewMode && (
                        <Button
                          className="w-fit shrink-0"
                          onClick={() => onEditClick(3)}
                          styleType="NONE"
                        >
                          <EditIcon iconColor="#2565C8" />
                        </Button>
                      )}
                    {showDocumentsNotesEditActions && (
                      <Button
                        className="w-fit shrink-0"
                        onClick={() => onEditClick(documentsAndNotesEditStep)}
                        styleType="NONE"
                      >
                        <EditIcon iconColor="#2565C8" />
                      </Button>
                    )}
                  </>
                }
                event={event}
                files={stepTwo?.files}
                onDocumentClick={handleDocumentClick}
                previewMode={previewMode}
              />
              <GrantSummaryPlainFields>
                <div className="flex w-full items-center justify-between gap-2">
                  <div className="min-w-0 flex-1">
                    <GrantSummaryPlainRow
                      label={<AppFormattedMessage id="ADDITIONAL_NOTES" />}
                      value={
                        <span
                          className={twMerge(
                            'max-w-[250px] break-words',
                            !previewMode ? 'max-w-[220px]' : 'max-w-[250px]',
                          )}
                        >
                          {stepTwo?.additionalNotes || '-'}
                        </span>
                      }
                    />
                  </div>
                  {isRenderFundraising &&
                    stepOne.eventDetails['fundraising-round']?.isOpen &&
                    !previewMode && (
                      <Button
                        className="w-fit shrink-0"
                        onClick={() => onEditClick(3)}
                        styleType="NONE"
                      >
                        <EditIcon iconColor="#2565C8" />
                      </Button>
                    )}
                  {showDocumentsNotesEditActions && (
                    <Button
                      className="w-fit shrink-0"
                      onClick={() => onEditClick(documentsAndNotesEditStep)}
                      styleType="NONE"
                    >
                      <EditIcon iconColor="#2565C8" />
                    </Button>
                  )}
                </div>
              </GrantSummaryPlainFields>
            </>
          )}
        </div>
      </div>

      <div
        className={twMerge(
          'flex h-9 w-full justify-start gap-3 px-6',
          previewMode && 'justify-end',
        )}
      >
        <Button
          className={twMerge(
            'w-fit bg-transparent px-4 py-[6px] text-sm font-[450] text-gray-700',
            previewMode && 'bg-brand-700 text-white',
          )}
          onClick={() => {
            handleCloseModal();
          }}
          styleType="NONE"
          type="button"
        >
          {previewMode ? <AppFormattedMessage id="CLOSE" /> : <AppFormattedMessage id="CANCEL" />}
        </Button>
      </div>
    </>
  );
};
